import { onMounted, type Ref } from "vue";
import { useStoryblokBridge, type ISbStoryData } from "@storyblok/vue";

interface StoryblokBridgeStoryOptions {
	/**
	 * Mirror the page's `resolve_links` so links in the live-updated story
	 * resolve the same way they do on the initial fetch.
	 */
	resolveLinks?: "url" | "story";
	/**
	 * Mirror the page's `resolve_relations` when the page uses them.
	 */
	resolveRelations?: string[];
}

/**
 * Live-syncs a story ref with the Storyblok Visual Editor bridge so the
 * preview reflects edits instantly — without saving.
 *
 * Why a composable instead of inlining `useStoryblokBridge`: the bridge
 * must be registered through `onMounted`, and lifecycle hooks have to be
 * set up BEFORE the first `await` in `<script setup>` (after an await the
 * active component instance is lost and the hook is dropped). Call this at
 * the top of setup, passing a ref that a later `await`ed fetch populates —
 * the hook is registered synchronously, and by the time it fires on mount
 * the fetch has resolved and `story.value.id` exists.
 *
 * The bridge script itself only loads inside the editor (the URL carries
 * `_storyblok_tk`), so for real visitors this is a no-op. Pages keep their
 * existing folder-aware fetch (`fetchStoryblokContentByFolder`); this only
 * adds the live-edit channel on top.
 */
export const useStoryblokBridgeStory = (
	story: Ref<ISbStoryData | null | undefined>,
	options: StoryblokBridgeStoryOptions = {}
): void => {
	onMounted(() => {
		const id = story.value?.id;
		if (!id) {
			return;
		}

		useStoryblokBridge(
			Number(id),
			(liveStory) => {
				story.value = liveStory;
			},
			options
		);
	});
};
